import React from 'react';
import {FormattedMessage} from 'react-intl';
import Select, {
  ClearIndicatorProps,
  components,
  ContainerProps,
  ControlProps,
  DropdownIndicatorProps,
  IndicatorsContainerProps,
  IndicatorSeparatorProps,
  InputProps,
  MenuProps,
  OptionProps,
  PlaceholderProps,
  Props as ReactSelectProps,
  ValueContainerProps
} from 'react-select';
import classNames from 'classnames';
import ButtonAction from '../ButtonAction';
import icon from '../../../assets/triangle-down.svg';
import clear from '../../../assets/icons/cancel-circle.svg';
import styles from './InputSelect.module.scss';

export interface InputSelectProps<Option = unknown, IsMulti extends boolean = false, > extends ReactSelectProps<Option, IsMulti, any> {
  label?: React.ReactNode,
  error?: React.ReactNode,
  touched?: boolean,
}

const SelectContainer = <Option = unknown>({children, className, ...props}: ContainerProps<Option>) => (
  <components.SelectContainer {...props} className={classNames(className, styles.selectContainerWrapper)}>
    {children}
  </components.SelectContainer>
);

const Control = <Option = unknown>({children, className, ...props}: ControlProps<Option>) => (
  <components.Control {...props} className={classNames(className, styles.selectContainer)}>
    {children}
  </components.Control>
);

const ValueContainer = <Option = unknown>({children, className, ...props}: ValueContainerProps<Option>) => (
  <components.ValueContainer {...props} className={classNames(className, styles.valueContainer)}>
    {children}
  </components.ValueContainer>
);

const Option = <Option = unknown>({children, className, ...props}: OptionProps<Option>) => (
  <components.Option {...props} className={classNames(className, styles.option, {
    [styles.focused]: props.isFocused,
    [styles.selected]: props.isSelected,
  })}>
    {children}
  </components.Option>
);

const IndicatorSeparator = <Option = unknown>({className, ...props}: IndicatorSeparatorProps<Option>) => (
  <components.IndicatorSeparator {...props} className={classNames(className, styles.indicatorSeparator)}/>
);

const IndicatorsContainer = <Option = unknown>({children, className, ...props}: IndicatorsContainerProps<Option>) => (
  <components.IndicatorsContainer {...props} className={classNames(className, styles.indicatorContainer)}>
    {children}
  </components.IndicatorsContainer>
);

const DropdownIndicator = <Option = unknown>({className, ...props}: DropdownIndicatorProps<Option>) => (
  <components.DropdownIndicator {...props} className={classNames(className, styles.ddIndicator)}>
    <img src={icon} alt="triangle-down"/>
  </components.DropdownIndicator>
);

const Placeholder = <Option = unknown>({children, className, ...props}: PlaceholderProps<Option>) => (
  <components.Placeholder {...props} className={classNames(className, styles.placeholder)}>
    {children}
  </components.Placeholder>
);

const Menu = <Option = unknown>({children, className, ...props}: MenuProps<Option>) => (
  <components.Menu {...props} className={classNames(className, styles.menu)}>
    {children}
  </components.Menu>
);

const ClearIndicator = <Option = unknown>({className, ...props}: ClearIndicatorProps<Option>) => (
  <components.ClearIndicator {...props} className={classNames(className, styles.clearIndicator)}>
    <ButtonAction
      variant={'icon'}
      icon={clear}
      customClassnames={{container: styles.btnReset}}
    />
  </components.ClearIndicator>
);

const Input = <Option = unknown>({children, className, ...props}: InputProps<Option>) => (
  <components.Input {...props} className={classNames(className, styles.input)}>
    {children}
  </components.Input>
);

const InputSelect = <OptionType, IsMulti extends boolean = false, >({
                                                                      label,
                                                                      touched,
                                                                      error,
                                                                      value,
                                                                      noOptionsMessage = () => <FormattedMessage id={'message-no-options'} defaultMessage={'No options'}/>,
                                                                      ...rest
                                                                    }: InputSelectProps<OptionType, IsMulti>) => {
  const showErrorCondition = touched && error && !value;

  return (
    <div className={styles.container}>
      <Select
        <OptionType, IsMulti, any>
        backspaceRemovesValue={true}
        isClearable={true}
        value={value}
        noOptionsMessage={noOptionsMessage}
        {...rest}
        components={{
          SelectContainer,
          Control,
          ValueContainer,
          IndicatorsContainer,
          IndicatorSeparator,
          DropdownIndicator,
          Option,
          Placeholder,
          Menu,
          ClearIndicator,
          Input
        }}
      />
      <div className={styles.label}>
        {label}
      </div>
      {showErrorCondition && (
        <div className={styles.errorContainer}>
          <div className={styles.error}>{error}</div>
        </div>
      )}
    </div>
  );
};

export default InputSelect;
